// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Locale from "./locale.js"

const modelClassData = {
  "attributes": {
    "approved": {
      "column": {
        "default": "0",
        "name": "approved",
        "null": false,
        "type": "boolean"
      },
      "name": "approved",
      "selected_by_default": null,
      "translated": null
    },
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "info": {
      "column": {
        "default": null,
        "name": "info",
        "null": true,
        "type": "text"
      },
      "name": "info",
      "selected_by_default": null,
      "translated": null
    },
    "key": {
      "column": {
        "default": null,
        "name": "key",
        "null": false,
        "type": "string"
      },
      "name": "key",
      "selected_by_default": null,
      "translated": null
    },
    "locale": {
      "column": {
        "default": null,
        "name": "locale",
        "null": false,
        "type": "string"
      },
      "name": "locale",
      "selected_by_default": null,
      "translated": null
    },
    "made_by_ai": {
      "column": {
        "default": "0",
        "name": "made_by_ai",
        "null": false,
        "type": "boolean"
      },
      "name": "made_by_ai",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "value": {
      "column": {
        "default": null,
        "name": "value",
        "null": true,
        "type": "text"
      },
      "name": "value",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "Translation",
  "collectionKey": "translations",
  "collectionName": "translations",
  "i18nKey": "translation",
  "camelizedLower": "translation",
  "name": "Translation",
  "nameDasherized": "translation",
  "pluralName": "translations",
  "ransackable_associations": [
    {
      "className": "Translation::Audit",
      "collectionName": "translation_audits",
      "foreignKey": "translation_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "TranslationAudit",
      "through": null
    },
    {
      "className": "Translation::Audit",
      "collectionName": "translation_audits",
      "foreignKey": "translation_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "TranslationAudit",
      "through": null
    },
    {
      "className": "Locale",
      "collectionName": "locales",
      "foreignKey": "locale",
      "name": "locale_model",
      "macro": "belongs_to",
      "resource_name": "Locale",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "approved",
      "column": {
        "default": "0",
        "name": "approved",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "id_value",
      "column": null
    },
    {
      "name": "info",
      "column": {
        "default": null,
        "name": "info",
        "null": true,
        "type": "text"
      }
    },
    {
      "name": "key",
      "column": {
        "default": null,
        "name": "key",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "locale",
      "column": {
        "default": null,
        "name": "locale",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "made_by_ai",
      "column": {
        "default": "0",
        "name": "made_by_ai",
        "null": false,
        "type": "boolean"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "value",
      "column": {
        "default": null,
        "name": "value",
        "null": true,
        "type": "text"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "Locale",
      "collectionName": "locales",
      "foreignKey": "locale",
      "name": "locale_model",
      "macro": "belongs_to",
      "resource_name": "Locale",
      "through": null
    }
  ],
  "paramKey": "translation",
  "primaryKey": "id"
}

/** Frontend model for Translation. */
export default class Translation extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {boolean} */
  approved() {
    return this.readAttributeUnderscore("approved")
  }

  /** @returns {boolean} */
  hasApproved() {
    const value = this.approved()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  info() {
    return this.readAttributeUnderscore("info")
  }

  /** @returns {boolean} */
  hasInfo() {
    const value = this.info()

    return this._isPresent(value)
  }

  /** @returns {string} */
  key() {
    return this.readAttributeUnderscore("key")
  }

  /** @returns {boolean} */
  hasKey() {
    const value = this.key()

    return this._isPresent(value)
  }

  /** @returns {string} */
  locale() {
    return this.readAttributeUnderscore("locale")
  }

  /** @returns {boolean} */
  hasLocale() {
    const value = this.locale()

    return this._isPresent(value)
  }

  /** @returns {boolean} */
  madeByAi() {
    return this.readAttributeUnderscore("made_by_ai")
  }

  /** @returns {boolean} */
  hasMadeByAi() {
    const value = this.madeByAi()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  value() {
    return this.readAttributeUnderscore("value")
  }

  /** @returns {boolean} */
  hasValue() {
    const value = this.value()

    return this._isPresent(value)
  }

  /** @returns {import("./locale.js").default | null} */
  localeModel() {
    return this._readBelongsToReflection({modelClass: Locale, reflectionName: "locale_model"})
  }

  /** @returns {Promise<import("./locale.js").default | null>} */
  loadLocaleModel() {
    if (!("locale" in this)) throw new Error("Foreign key method wasn't defined: locale")

    const id = this.locale()
    const ransack = {}

    ransack["locale_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "locale_model",
        model: this,
        modelClass: Locale
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./locale.js").default | null>} */
  ensureLocaleModelLoaded() {
    return this.ensureAssociationLoaded("locale_model")
  }
}
